import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const RESERVED_SUBDOMAINS = new Set(['admin', 'www', 'api', 'app', 'mail', 'localhost']);
const PLATFORM_HOSTS = ['builtwithrocket.new', 'rocket.new', 'amazonaws.com', 'vercel.app', 'netlify.app', 'github.io', 'localhost'];

const TenantContext = createContext({});

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};

const extractSlugFromHost = (hostname) => {
  if (!hostname) return null;
  const isPlatformHost = PLATFORM_HOSTS?.some((h) => hostname === h || hostname?.endsWith(`.${h}`));
  if (isPlatformHost) return null;

  const hostParts = hostname?.split('.');
  if (hostParts?.length < 3) return null;
  const subdomain = hostParts?.[0];
  if (!subdomain || RESERVED_SUBDOMAINS?.has(subdomain)) return null;
  return subdomain;
};

export const TenantProvider = ({ children }) => {
  const location = useLocation();
  const [restaurantId, setRestaurantId] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [slug, setSlug] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isNotFound, setIsNotFound] = useState(false);
  const [isAdminHost, setIsAdminHost] = useState(false);

  useEffect(() => {
    let mounted = true;

    const resolveTenant = async () => {
      const params = new URLSearchParams(location.search);
      const directRestaurantId = params?.get('restaurant_id');
      const slugParam = params?.get('slug');
      const hostname = window.location?.hostname || '';
      const subdomainSlug = extractSlugFromHost(hostname);
      const resolvedSlug = slugParam || subdomainSlug;

      if (directRestaurantId) {
        if (!mounted) return;
        setRestaurantId(directRestaurantId);
        setRestaurant(null);
        setSlug(resolvedSlug || null);
        setIsAdminHost(false);
        setIsNotFound(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setIsNotFound(false);

      if (hostname === 'admin.scann3r.ca' || resolvedSlug === 'admin') {
        if (!mounted) return;
        setIsAdminHost(true);
        setSlug('admin');
        setRestaurant(null);
        setRestaurantId(null);
        setIsLoading(false);
        return;
      }

      if (!resolvedSlug) {
        if (!mounted) return;
        setRestaurantId(null);
        setRestaurant(null);
        setSlug(null);
        setIsAdminHost(false);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase?.rpc('get_restaurant_by_slug', { p_slug: resolvedSlug });
      if (!mounted) return;

      if (error || !data?.[0]?.id) {
        setRestaurantId(null);
        setRestaurant(null);
        setSlug(resolvedSlug);
        setIsNotFound(true);
      } else {
        setRestaurantId(data?.[0]?.id);
        setRestaurant(data?.[0]);
        setSlug(data?.[0]?.slug);
      }
      setIsAdminHost(false);
      setIsLoading(false);
    };

    resolveTenant();
    return () => {
      mounted = false;
    };
  }, [location.pathname, location.search, location.key]);

  const value = useMemo(
    () => ({
      restaurantId,
      restaurant,
      slug,
      isLoading,
      isNotFound,
      isAdminHost,
      reservedSubdomains: Array.from(RESERVED_SUBDOMAINS),
    }),
    [restaurantId, restaurant, slug, isLoading, isNotFound, isAdminHost],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};
